import { useMemo } from "react";
import { useGetList } from "react-admin";
import type { DuplicateClaimantPair } from "@/types/duplicateClaimantPair";

/**
 * Fetches the ACTIVE suspected-duplicate pairs touching a claimant (Story #6718,
 * backend Story #6518).
 *
 * Uses `GET /claimants/{claimantId}/duplicate-pairs`, the same path-style
 * sub-resource convention as `useDeductibleHistory`.
 *
 * THE ENDPOINT TAKES NO QUERY STRING. It reads `claimantId` from the path and the
 * partner from context, and calls `ListForClaimant(ctx, claimantID, partnerID)` —
 * no limit, no offset, no status, no severity, no sort. (An earlier version of this
 * comment listed the REVIEW LIST's parameters, which is a different handler; that
 * mistake is what made a page-size cap look real. See below.)
 *
 * "Active" is the BACKEND's definition and is deliberately not re-implemented
 * here: that endpoint returns `open` and `confirmed` only, excludes pairs whose
 * counterpart claimant has been soft-deleted, and is partner-scoped. Filtering
 * again in the client would create a second opinion about pair state, and the
 * two would drift — which is the whole reason #6718 has no dismiss action.
 *
 * `confirmed` is included on purpose. A confirmed-but-unmerged pair is precisely
 * when this claimant's accumulation is still wrong: someone has agreed it is a
 * duplicate and the data is still split. Hiding the banner at that moment would
 * remove it exactly when it became certain.
 */
/**
 * Highest combined-spend-to-deductible ratio first, so the most consequential pair
 * leads when a claimant has several.
 *
 * Sorted CLIENT-SIDE, and it AGREES with the server rather than substituting for it.
 * `ListClaimantDuplicatePairsForClaimant` already ends
 * `ORDER BY severity_ratio DESC NULLS LAST, created_datetime DESC` — same key, same
 * direction, and the `-Infinity` below puts a missing ratio exactly where NULLS LAST
 * does. So the two cannot disagree, and the banner stays correct if someone later
 * points this hook at the filterable review-list endpoint instead.
 *
 * The client sort is still needed: for a 3-part sub-resource path the dataProvider
 * never puts `sort`/`order` on the query string at all (`dataProvider.ts:376-403`),
 * so passing them was dead configuration that read as implemented ordering.
 *
 * `severity_ratio` is a decimal STRING on the wire (`numeric(14,4)`), so it needs a
 * numeric parse rather than the lexicographic compare a naive sort would apply.
 */
const bySeverityRatioDesc = (a: DuplicateClaimantPair, b: DuplicateClaimantPair): number => {
  const ratio = (pair: DuplicateClaimantPair) => {
    const parsed = Number(pair.severity_ratio);
    return Number.isFinite(parsed) ? parsed : -Infinity;
  };
  return ratio(b) - ratio(a);
};

export const useClaimantDuplicatePairs = (claimantId?: string) => {
  // No pagination argument. The endpoint ignores limit/offset and its SQL carries no
  // LIMIT, so it returns every active pair; requesting a page size would send
  // parameters nothing reads and imply a cap that does not exist.
  const { data, isLoading, error, refetch } = useGetList<DuplicateClaimantPair>(
    `claimants/${claimantId}/duplicate-pairs`,
    { filter: {} },
    { enabled: !!claimantId }
  );

  const pairs = useMemo(() => [...(data ?? [])].sort(bySeverityRatioDesc), [data]);

  return {
    pairs,
    // NO separate `total`. It was added to stop the headline under-reporting a paged
    // result, but this endpoint cannot page: the handler uses HandleResponseWithNoCount
    // so the body is a BARE ARRAY, and the dataProvider then derives
    // `total = json.length` for that shape (`dataProvider.ts:385`). total and
    // pairs.length were the same number by construction, and the "showing N of M"
    // overflow notice they backed could never render (review !4646 thread 27721).
    //
    // The finding that prompted it is real for the REVIEW LIST endpoint, which does
    // paginate and does return a count. It was transplanted onto this one.
    // isLoading and error are returned SEPARATELY rather than collapsed into one
    // "unknown" flag: the banner treats them differently on purpose — silent while
    // loading, explicit notice on failure — so a combined flag would lose the
    // distinction the fail-visible behaviour depends on.
    isLoading,
    error,
    refetch,
    hasSuspectedDuplicates: pairs.length > 0,
  };
};

/**
 * Returns the OTHER claimant in a pair, given the one being viewed.
 *
 * The pair is stored in canonical UUID order (`claimant_a_id < claimant_b_id`)
 * purely so (A,B) and (B,A) are one row — that ordering carries no meaning about
 * which record is the "real" one. So the banner cannot assume the viewed
 * claimant is side A; users often arrive at the SMALLER row, which is what makes
 * the accumulation look wrong in the first place.
 */
export const counterpartOf = (pair: DuplicateClaimantPair, viewedClaimantId: string) => {
  // Membership is ASSERTED, not inferred. `viewedIsA ? … : …` alone treats "matches
  // neither side" as "must be side B", which silently resolves every field to the wrong
  // half — presenting the counterpart's spend as this record's own, the exact inversion
  // this function exists to prevent. A pair that doesn't touch the viewed claimant is a
  // bug upstream (the endpoint is scoped to this claimant), so return null and let the
  // caller skip it: absent beats confidently backwards.
  if (pair.claimant_a_id !== viewedClaimantId && pair.claimant_b_id !== viewedClaimantId) return null;

  const viewedIsA = pair.claimant_a_id === viewedClaimantId;
  return {
    counterpartId: viewedIsA ? pair.claimant_b_id : pair.claimant_a_id,
    counterpartSource: viewedIsA ? ("claimant_b_id" as const) : ("claimant_a_id" as const),
    viewedPaidAmount: viewedIsA ? pair.claimant_a_paid_amount : pair.claimant_b_paid_amount,
    counterpartPaidAmount: viewedIsA ? pair.claimant_b_paid_amount : pair.claimant_a_paid_amount,
  };
};
